import {
  MAX_POOL_SIZE,
  PoolSizeAdjustmentDirection,
  PoolSizeAdjustmentInput,
  PoolSizeAdjustmentIssue,
  PoolSizeAdjustmentReason,
  resolvePoolSizeAdjustment,
} from 'shared';
import { z } from 'zod';

import { StringKey } from '../../../lang/index';
import { getTranslation } from '../../../utils/get-translation.util';

export type PoolSizeContext = {
  currentPoolSize: number;
  /** Shares already granted from the pool — the size can never drop below them. */
  grantedShares: number;
};

const poolSizeIssueMessageKey: Record<PoolSizeAdjustmentIssue, StringKey> = {
  [PoolSizeAdjustmentIssue.BELOW_GRANTED]: 'POOL_SIZE_BELOW_GRANTED_COUNT',
  [PoolSizeAdjustmentIssue.EMPTY_POOL]: 'POOL_SIZE_EMPTY',
  [PoolSizeAdjustmentIssue.INVALID_AMOUNT]: 'POOL_SIZE_INVALID_AMOUNT',
  [PoolSizeAdjustmentIssue.TOO_LARGE]: 'POOL_SIZE_TOO_LARGE',
  [PoolSizeAdjustmentIssue.UNCHANGED]: 'POOL_SIZE_UNCHANGED',
};

export const getPoolSizeIssueMessage = (
  issue: PoolSizeAdjustmentIssue,
  { grantedShares }: Pick<PoolSizeContext, 'grantedShares'>,
): string =>
  getTranslation(poolSizeIssueMessageKey[issue], {
    count: grantedShares.toLocaleString('en-US'),
    max: MAX_POOL_SIZE.toLocaleString('en-US'),
  });

/** The number input emits digits only (separators stripped); empty means nothing entered yet. */
export const parseShareAmount = (value?: string): number => (value ? Number(value) : Number.NaN);

const addPoolSizeIssue = (
  context: PoolSizeContext,
  input: PoolSizeAdjustmentInput,
  path: string,
  ctx: z.RefinementCtx,
) => {
  const { issue } = resolvePoolSizeAdjustment({ ...context, input });

  if (issue) {
    ctx.addIssue({
      code: 'custom',
      message: getPoolSizeIssueMessage(issue, context),
      path: [path],
    });
  }
};

// The shared rule is the one the backend enforces, so the confirm button only enables for a
// request the API accepts.
export const createFormalAdjustmentSchema = (context: PoolSizeContext) =>
  z
    .object({
      direction: z.enum(PoolSizeAdjustmentDirection),
      sharesCount: z.string(),
    })
    .superRefine(({ direction, sharesCount }, ctx) =>
      addPoolSizeIssue(
        context,
        {
          reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT,
          direction,
          sharesCount: parseShareAmount(sharesCount),
        },
        'sharesCount',
        ctx,
      ),
    );

export const createDataEntryCorrectionSchema = (context: PoolSizeContext) =>
  z
    .object({
      dilutedSharesCount: z.string(),
    })
    .superRefine(({ dilutedSharesCount }, ctx) =>
      addPoolSizeIssue(
        context,
        {
          reason: PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION,
          dilutedSharesCount: parseShareAmount(dilutedSharesCount),
        },
        'dilutedSharesCount',
        ctx,
      ),
    );

export type FormalAdjustmentForm = z.infer<ReturnType<typeof createFormalAdjustmentSchema>>;
export type DataEntryCorrectionForm = z.infer<ReturnType<typeof createDataEntryCorrectionSchema>>;
